import { validateSchema, type ValidationResult } from "@jasper/forms";
import { z } from "zod";

import type {
  BulkTeethSubmitOptions,
  ConfigurableProduct,
  ProductStepProps,
} from "../types";
import {
  ItemScopeEnum,
  ItemTypeEnum,
  mixedTeethSchema,
  ProductTypeEnum,
  type ItemType,
  type ProductType,
} from "@jasper/client-models";
import {
  GenericTeethStep,
  type TeethStepValidatorInput,
} from "./GenericTeethStep";
import { useTranslation } from "react-i18next";
import {
  useOrderConfiguration,
  type ProductItemArgs,
  type UpdateProductArgs,
} from "@jasper/use-cases";
import { notifications } from "@mantine/notifications";

export interface CrownTeethStepProps extends ProductStepProps {
  type: "FIXED" | "IMPLANT";
}

const validateSelection = ({
  value,
  isBulkSelection,
  setTeethInError,
}: TeethStepValidatorInput): Record<string, unknown> | undefined => {
  setTeethInError([]);

  if (isBulkSelection && value.groupedTeeth.length > 1) {
    const flattenedTeeth = value.groupedTeeth.flat();
    if (
      flattenedTeeth.length !== value.teeth.length ||
      flattenedTeeth.some(tooth => !value.teeth.includes(tooth))
    ) {
      return {
        teeth: "unexpected",
      };
    }

    const result = value.groupedTeeth.reduce<
      ValidationResult<{ teeth: number[] }>
    >(
      (acc, group) => {
        const result = validateSchema(
          z.object({
            teeth: mixedTeethSchema,
          }),
          { teeth: group }
        );

        if (!result.success) {
          setTeethInError(prev => [...prev, ...group]);
          return {
            success: false,
            errors: {
              ...("errors" in acc ? acc.errors : {}),
              ...result.errors,
            },
          };
        }

        return acc;
      },
      { success: true, data: value }
    );

    return result.success ? undefined : result.errors;
  }

  const result = validateSchema(
    z.object({
      teeth: mixedTeethSchema,
    }),
    value
  );

  return result.success ? undefined : result.errors;
};

type ProductItem = ConfigurableProduct["items"][number];

interface CrownTypeConfig {
  singleProductType: ProductType;
  splintedProductType: ProductType;
  buildToothItems: (args: {
    tooth: number;
    product: ConfigurableProduct;
    itemType: ItemType;
  }) => ProductItemArgs[];
}

// Prefer the item already set on this tooth, otherwise reuse the first matching item of the product
const findExistingItem = (
  product: ConfigurableProduct,
  tooth: number,
  predicate: (item: ProductItem) => boolean = () => true
) =>
  product.items.find(
    item =>
      item.scope === ItemScopeEnum.tooth &&
      item.tooth === tooth &&
      predicate(item)
  ) ?? product.items.find(predicate);

const isAbutment = (item: ProductItem) =>
  item.type === ItemTypeEnum.abutment ||
  item.type === ItemTypeEnum.customAbutment;

const isImplantCrown = (item: ProductItem) =>
  item.type === ItemTypeEnum.implantCrown;

const crownTypeConfigs: Record<CrownTeethStepProps["type"], CrownTypeConfig> = {
  FIXED: {
    singleProductType: ProductTypeEnum.crown,
    splintedProductType: ProductTypeEnum.splintedCrown,
    buildToothItems: ({ tooth, product, itemType }) => {
      const existingItem = findExistingItem(product, tooth);

      return [
        {
          tooth,
          type: itemType,
          materialCharacteristics: existingItem?.materialCharacteristics,
          inlayCoreCharacteristics: existingItem?.inlayCoreCharacteristics,
        },
      ];
    },
  },
  IMPLANT: {
    singleProductType: ProductTypeEnum.implantCrown,
    splintedProductType: ProductTypeEnum.splintedCrownImplant,
    buildToothItems: ({ tooth, product }) => {
      const existingImplantCrown = findExistingItem(
        product,
        tooth,
        isImplantCrown
      );
      const existingAbutment = findExistingItem(product, tooth, isAbutment);

      return [
        {
          tooth,
          type: ItemTypeEnum.implantCrown,
          materialCharacteristics:
            existingImplantCrown?.materialCharacteristics,
          implantedItemCharacteristics:
            existingImplantCrown?.implantedItemCharacteristics,
        },
        {
          tooth,
          type: existingAbutment?.type ?? ItemTypeEnum.abutment,
          implantCharacteristics: existingAbutment?.implantCharacteristics,
          abutmentCharacteristics: existingAbutment?.abutmentCharacteristics,
        },
      ];
    },
  },
};

const getProductType = (config: CrownTypeConfig, teeth: number[]) =>
  teeth.length > 1 ? config.splintedProductType : config.singleProductType;

const createApplyTeethSelection =
  (config: CrownTypeConfig) =>
  ({
    teeth,
    product,
    itemType,
  }: {
    teeth: number[];
    product: ConfigurableProduct;
    itemType: ItemType;
  }): UpdateProductArgs => ({
    productId: product.id,
    teeth,
    items: teeth.flatMap(tooth =>
      config.buildToothItems({ tooth, product, itemType })
    ),
    type: getProductType(config, teeth),
  });

const createBulkSubmitOptions = (
  config: CrownTypeConfig
): BulkTeethSubmitOptions => ({
  mutationStrategy: {
    getTargetProductType: teeth => getProductType(config, teeth),
    canReuseProduct: ({ currentProductType, targetProductType }) =>
      currentProductType === targetProductType,
  },
});

const crownTeethSelection = {
  FIXED: {
    applyTeethSelection: createApplyTeethSelection(crownTypeConfigs.FIXED),
    bulkSubmitOptions: createBulkSubmitOptions(crownTypeConfigs.FIXED),
  },
  IMPLANT: {
    applyTeethSelection: createApplyTeethSelection(crownTypeConfigs.IMPLANT),
    bulkSubmitOptions: createBulkSubmitOptions(crownTypeConfigs.IMPLANT),
  },
} satisfies Record<CrownTeethStepProps["type"], unknown>;

export const CrownTeethStep = ({
  bulk,
  type,
  ...props
}: CrownTeethStepProps) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.teeth",
  });

  const { updateProduct } = useOrderConfiguration({
    orderId: props.orderId,
  });

  const { applyTeethSelection, bulkSubmitOptions } = crownTeethSelection[type];

  return (
    <GenericTeethStep
      {...props}
      bulk={
        bulk
          ? {
              ...bulk,
              submissionOptions: {
                ...bulk.submissionOptions,
                ...bulkSubmitOptions,
              },
            }
          : undefined
      }
      validateSelection={validateSelection}
      applyTeethSelection={applyTeethSelection}
      onSave={async teeth => {
        try {
          await updateProduct(
            applyTeethSelection({
              teeth,
              product: props.product,
              itemType: props.itemType,
            })
          );
        } catch {
          notifications.show({
            title: t("errorNotificationTitle", { keyPrefix: "common" }),
            message: t("errorNotificationMessage", { keyPrefix: "common" }),
            color: "red",
          });
          return;
        }
      }}
    />
  );
};
